import { useState, useEffect } from 'react';
import { deriveTeamColors, validateTeamColors } from '../utils/colorUtils';
import { getDefaultColors } from '../config/teamColorPalette';

/**
 * Hook para gestionar los colores de las parejas
 * Maneja localStorage y sincronización entre componentes
 */
const useTeamColors = () => {
  const [teamColors, setTeamColors] = useState(() => {
    // Inicializar desde localStorage o usar defaults
    const defaults = getDefaultColors();
    const team1Color = localStorage.getItem('team1Color') || defaults.team1.hex;
    const team2Color = localStorage.getItem('team2Color') || defaults.team2.hex;

    return {
      team1: deriveTeamColors(team1Color),
      team2: deriveTeamColors(team2Color),
    };
  });

  useEffect(() => {
    // Listener para sincronizar cambios de color entre componentes
    const handleTeamColorsChanged = (event) => {
      const { team1Color, team2Color } = event.detail;
      setTeamColors({
        team1: deriveTeamColors(team1Color),
        team2: deriveTeamColors(team2Color),
      });
    };

    window.addEventListener('teamColorsChanged', handleTeamColorsChanged);
    return () => {
      window.removeEventListener('teamColorsChanged', handleTeamColorsChanged);
    };
  }, []);

  /**
   * Actualiza el color de una pareja
   * @param {number} teamNumber - Número de pareja (1 o 2)
   * @param {string} hexColor - Color en formato hex
   */
  const updateTeamColor = (teamNumber, hexColor) => {
    const defaults = getDefaultColors();
    const otherTeam = teamNumber === 1 ? 2 : 1;
    const otherTeamKey = `team${otherTeam}Color`;
    const otherTeamColor = localStorage.getItem(otherTeamKey);

    // Validar que los colores sean diferentes
    if (otherTeamColor && !validateTeamColors(hexColor, otherTeamColor)) {
      console.warn('Los colores de las parejas deben ser diferentes');
      return false;
    }

    // Guardar en localStorage
    const teamKey = `team${teamNumber}Color`;
    localStorage.setItem(teamKey, hexColor);

    // Actualizar estado local con valores por defecto si no existen en localStorage
    const team1Color = teamNumber === 1 
      ? hexColor 
      : (localStorage.getItem('team1Color') || defaults.team1.hex);
    const team2Color = teamNumber === 2 
      ? hexColor 
      : (localStorage.getItem('team2Color') || defaults.team2.hex);

    const newTeamColors = {
      team1: deriveTeamColors(team1Color),
      team2: deriveTeamColors(team2Color),
    };

    setTeamColors(newTeamColors);

    // Emitir evento para sincronizar otros componentes
    window.dispatchEvent(new CustomEvent('teamColorsChanged', {
      detail: { team1Color, team2Color }
    }));

    return true;
  };

  /**
   * Actualiza ambos colores de parejas simultáneamente
   * @param {string} team1Color - Color pareja 1 en hex
   * @param {string} team2Color - Color pareja 2 en hex
   */
  const updateBothTeamColors = (team1Color, team2Color) => {
    // Validar que sean diferentes
    if (!validateTeamColors(team1Color, team2Color)) {
      console.warn('Los colores de las parejas deben ser diferentes');
      return false;
    }

    // Guardar en localStorage
    localStorage.setItem('team1Color', team1Color);
    localStorage.setItem('team2Color', team2Color);

    // Actualizar estado local
    const newTeamColors = {
      team1: deriveTeamColors(team1Color),
      team2: deriveTeamColors(team2Color),
    };

    setTeamColors(newTeamColors);

    // Emitir evento para sincronizar otros componentes
    window.dispatchEvent(new CustomEvent('teamColorsChanged', {
      detail: { team1Color, team2Color }
    }));

    return true;
  };

  /**
   * Resetea los colores a los valores por defecto
   */
  const resetToDefaults = () => {
    const defaults = getDefaultColors();
    updateBothTeamColors(defaults.team1.hex, defaults.team2.hex);
  };

  /**
   * Obtiene los colores actuales en formato hex simple
   */
  const getCurrentHexColors = () => ({
    team1: teamColors.team1.main,
    team2: teamColors.team2.main,
  });

  return {
    teamColors,
    updateTeamColor,
    updateBothTeamColors,
    resetToDefaults,
    getCurrentHexColors,
  };
};

export default useTeamColors;
